import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex } from "../../components/Flex";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { formSliceSelectors } from "../../redux/forms/form.selector";
import styled, { useTheme } from "styled-components";
import { Spacing } from "../../theme/Spacing";
import { SubHeader } from "../../components/SubHeader";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { Trans, useTranslation } from "react-i18next";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { useAtiraFormContext } from "./context/form-context";
import { FormsFolderCreateModal } from "./components/form-folders/FormsFolderCreateModal";
import { WarningModal } from "../../components/WarningModal";
import { AtiraToast } from "../../utils/AtiraToast";
import { formActions } from "../../redux/forms/form.slice";
import isEmpty from "lodash/isEmpty";
import { FormsContent } from "./components/FormsContent";
import { FormDrawers } from "./FormDrawers";
import { FormProvider, useForm } from "react-hook-form";
import { GetFormFoldersDto } from "../../model/form/dto/GetFormFoldersDto";
import { thresholdSliceSelectors } from "../../redux/threshold/threshold.selector";
import { canCreateForm, FORMS_CREATE_PARAM } from "./utils/FormUtils";
import { TestIDs } from "../../tests/types/testIds";
import { thresholdActions } from "../../redux/threshold/threshold.slice";
import { entryActions } from "../../redux/entry/entry.slice";
import { permissionActions } from "../../redux/permission/permission.slice";
import { moduleActions } from "../../redux/module/module.slice";
import { EntityIcons } from "../../utils/GlobalIcons";
import { UserFormsFilters } from "./components/UserFormsFilters";
import { faFolder } from "@fortawesome/free-solid-svg-icons/faFolder";
import { Button } from "../../components/Button";
import { Shadow } from "../../theme/Shadow";
import { Rounded } from "../../theme/Rounded";
import { FoldersContent } from "./FoldersContent";
import { TabKeys } from "./types/FormTabs.enum";
import { useDrawersContext } from "../tasks/drawers-context";
import { Badge, MenuProps } from "../../utils/local-antd";
import { GetUserFormsDto } from "../../model/form/dto/GetUserFormsDto";
import { SortOptions } from "../../model/shared/enum/SortOptions.enum";
import { FormType } from "../../model/form/types/FormType.enum";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";

const TabButtonsWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.lightSub};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.lg};
  height: 2.3rem;
  width: fit-content;
  border: ${({ theme }) => `1px solid ${theme.sub}`};
`;

const TabButton = styled(Button)<{ active: boolean }>`
  border-radius: ${Rounded.md};
  height: 100%;
  padding: 0 ${Spacing.m};
  font-weight: 600;
  margin: 0;
  background-color: ${({ theme, active }) =>
    theme[active ? "white" : "transparent"]} !important;
  color: ${({ theme, active }) =>
    theme[active ? "black" : "darkerSub"]} !important;
  box-shadow: ${(props) => (props.active ? Shadow.SM : "none")} !important;

  &:hover {
    color: ${({ theme }) => theme.textColor} !important;
  }

  svg {
    color: ${({ theme, active }) => theme[active ? "main" : "darkerSub"]};
  }
`;

const FormsAndFolders: React.FC = () => {
  const [folderDeleteLoading, setFolderDeleteLoading] = useState(false);

  const activeTab = useAppSelector(formSliceSelectors.selectActiveTab);
  const dispatch = useAppDispatch();
  const formMethods = useForm<
    GetFormFoldersDto & GetUserFormsDto & { type: FormType | null }
  >({
    defaultValues: { sort: SortOptions.DESC },
  });
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    setFormsFolderCreateVisible,
    formsFolderDeleteVisible,
    setFormsFolderDeleteVisible,
    selectedFolder,
    setFormCreateEditDrawerVisible,
    setSelectedForm,
  } = useAtiraFormContext();
  const { setSubToUnlock } = useDrawersContext();

  const folders = useAppSelector(formSliceSelectors.selectFolders);
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.permissions.permissions;

  const [searchParams, setSearchParams] = useSearchParams();

  const reachedFoldersLimit = Boolean(folders?.length && folders.length >= 6);

  const canCreate = canCreateForm(thresholds);

  const onCreateForm = useCallback(() => {
    if (!canCreate) {
      setSubToUnlock({
        visible: true,
        title: t("subToUnlock.specific.formsCount", {
          limit: thresholds?.total?.FORMS,
        }),
      });
      return;
    }

    setSelectedForm(null);
    setFormCreateEditDrawerVisible(true);
  }, [
    canCreate,
    setSubToUnlock,
    t,
    thresholds?.total?.FORMS,
    setSelectedForm,
    setFormCreateEditDrawerVisible,
  ]);

  // The dashboard's "Create your first form" links here; wait for the
  // thresholds so the plan limit check sees real numbers
  useEffect(() => {
    if (!searchParams.has(FORMS_CREATE_PARAM) || !thresholds) {
      return;
    }

    onCreateForm();
    setSearchParams(
      (params) => {
        params.delete(FORMS_CREATE_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, thresholds, onCreateForm]);

  const onDelete = async () => {
    try {
      if (isEmpty(selectedFolder)) {
        return;
      }

      setFolderDeleteLoading(true);

      await dispatch(
        formActions.deleteFolder({
          formFolderId: selectedFolder._id,
          userId,
        }),
      ).unwrap();
      await dispatch(formActions.getFolders({ userId })).unwrap();

      setFormsFolderDeleteVisible(false);

      AtiraToast.success(t("forms.folder.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setFolderDeleteLoading(false);
    }
  };

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const onCreateFolder = () => {
      if (reachedFoldersLimit) {
        setSubToUnlock({
          visible: true,
          title: t("subToUnlock.specific.foldersCount", { limit: 6 }),
        });
        return;
      }

      setFormsFolderCreateVisible(true);
    };

    return [
      {
        key: "create-form",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text color="main">{t("forms.subheader.actions.create")}</Text>
          </Flex>
        ),
        onClick: onCreateForm,
        hide: userPermissions?.FORM.CREATE !== true,
        "data-testid": TestIDs.forms.subHeader.dropdown.option("create-form"),
      },
      {
        key: "create-folder",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text color="main">
              {t("forms.folders.subheader.actions.create")}
            </Text>
          </Flex>
        ),
        onClick: onCreateFolder,
        hide: userPermissions?.FORM.UPDATE !== true,
        "data-testid": TestIDs.forms.subHeader.dropdown.option("create-folder"),
      },
    ].filter((action) => !action.hide);
  }, [
    onCreateForm,
    reachedFoldersLimit,
    setFormsFolderCreateVisible,
    setSubToUnlock,
    t,
    userPermissions?.FORM.CREATE,
    userPermissions?.FORM.UPDATE,
  ]);

  useEffect(() => {
    dispatch(formActions.getForms({ userId }));
    dispatch(formActions.getFolders({ userId }));
    dispatch(thresholdActions.getThresholds({ userId }));
    dispatch(entryActions.getUserTableSettings({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(moduleActions.getAllModuleDefinitions({ userId }));
  }, [dispatch, userId]);

  return (
    <FormProvider {...formMethods}>
      <Flex flexDirection="column">
        <SubHeader
          title={
            <Trans
              i18nKey="subheader.forms"
              components={[
                <Badge
                  style={{ marginBlockEnd: Spacing.s }}
                  count={forms?.filter((form) => !form.orphan)?.length || 0}
                  color={theme.main}
                />,
              ]}
            />
          }
          extra={
            subHeaderActions.length ? (
              <AtiraDropdown
                items={subHeaderActions}
                placement="bottomRight"
                trigger={["click"]}
                destroyOnHidden
                dataTestId={TestIDs.forms.subHeader.dropdown.open}
              >
                <Button
                  icon={faBars}
                  color="black"
                  padding="s"
                  backgroundColor="white"
                  hoverBackgroundColor="lightMain"
                  hover
                >
                  {t("common.options")}
                </Button>
              </AtiraDropdown>
            ) : null
          }
          icon={EntityIcons.Forms}
        />

        <Flex alignItems="center" gap="s" marginBottom="s">
          <UserFormsFilters />

          <TabButtonsWrapper>
            <TabButton
              icon={EntityIcons.Forms}
              onClick={() => dispatch(formActions.setActiveTab(TabKeys.FORMS))}
              active={activeTab === TabKeys.FORMS}
              dataTestId={TestIDs.forms.tabs.forms}
            >
              {t("forms.tabs.forms")}
            </TabButton>

            <TabButton
              icon={faFolder}
              onClick={() =>
                dispatch(formActions.setActiveTab(TabKeys.FOLDERS))
              }
              active={activeTab === TabKeys.FOLDERS}
              dataTestId={TestIDs.forms.tabs.folders}
            >
              {t("forms.tabs.folders")}
            </TabButton>
          </TabButtonsWrapper>
        </Flex>

        {activeTab === TabKeys.FORMS ? <FormsContent /> : <FoldersContent />}

        <FormsFolderCreateModal />

        <WarningModal
          title={t("forms.folder.delete.title", {
            name: selectedFolder?.name,
          })}
          description={t("forms.folder.delete.description")}
          loading={folderDeleteLoading}
          isOpen={formsFolderDeleteVisible}
          onClose={() => setFormsFolderDeleteVisible(false)}
          onConfirm={onDelete}
          confirmButtonDataTestId={TestIDs.forms.folders.delete.confirmButton}
        />

        <FormDrawers />
      </Flex>
    </FormProvider>
  );
};

export default FormsAndFolders;
